/**
 * The template language rule actions use in their free-text fields, and the
 * action-set expansion that resolves half of it.
 *
 * This mirrors `backend/src/auto_transaction/template.rs` and the
 * `expand_actions` half of `library.rs`. The backend is the authority — it is
 * what actually writes the transaction — but the frontend needs the same
 * answers for two things the server can't help with: showing what a cited set
 * will do *while you type the values*, and finding the "ask user" prompts
 * hiding inside a cited set before the rule is applied.
 *
 * One deliberate divergence: where the backend treats an unfilled input or an
 * unknown placeholder as an error and refuses to run the rule, this leaves the
 * `{…}` visible in the rendered text. Nothing here decides whether a rule
 * applies, so showing the hole is more useful than throwing — and the hole is
 * also reported as a rule problem, with the badge to match.
 */
import type {
  ActionSetDto,
  ActionSetId,
  ActionSetRef,
  EditActionDto,
  FieldName,
} from "../api/types";

/** Marks a placeholder as a transaction field rather than an action set input. */
export const BUILTIN_PREFIX = "$";

/**
 * The transaction fields a `{$…}` placeholder can name. Kept in the order the
 * editor offers them, and matching `FieldName` on the wire.
 */
export const TEMPLATE_BUILTINS: readonly FieldName[] = [
  "source",
  "merchant",
  "description",
  "group",
  "amount",
  "expenseType",
  "datePaid",
  "kid",
  "payToAccount",
];

export type TemplateProblem =
  | { type: "malformed"; text: string }
  | { type: "unknownPlaceholder"; name: string };

type Token =
  | { kind: "literal"; source: string }
  | { kind: "placeholder"; name: string; source: string };

/** Text with no braces is not a template and needs no parse. */
const isPlain = (text: string) => !text.includes("{") && !text.includes("}");

/** `null` for a malformed template — see the grammar note in the Rust module. */
const tokenize = (text: string): Token[] | null => {
  const out: Token[] = [];
  let literalStart = 0;
  let i = 0;

  while (i < text.length) {
    const c = text[i];
    if ((c === "{" || c === "}") && text[i + 1] === c) {
      // An escape stays inside the literal run; phase two resolves it.
      i += 2;
      continue;
    }
    if (c !== "{") {
      i += 1;
      continue;
    }

    const close = text.indexOf("}", i);
    if (close === -1) return null;
    const name = text.slice(i + 1, close).trim();
    if (name === "" || name.includes("{")) return null;

    if (literalStart < i) {
      out.push({ kind: "literal", source: text.slice(literalStart, i) });
    }
    out.push({ kind: "placeholder", name, source: text.slice(i, close + 1) });
    literalStart = close + 1;
    i = close + 1;
  }

  if (literalStart < text.length) {
    out.push({ kind: "literal", source: text.slice(literalStart) });
  }
  return out;
};

const unescape = (literal: string) =>
  literal.replace(/\{\{|\}\}/g, (pair) => pair[0]);

/**
 * Phase one: resolve action set inputs, leave built-ins and escapes exactly as
 * written. An input with no value keeps its placeholder so the preview shows
 * the reader which box is still empty.
 */
export const renderInputs = (
  text: string,
  valueOf: (name: string) => string | undefined,
): string => {
  if (isPlain(text)) return text;
  const tokens = tokenize(text);
  if (!tokens) return text;

  return tokens
    .map((token) => {
      if (token.kind === "literal") return token.source;
      if (token.name.startsWith(BUILTIN_PREFIX)) return token.source;
      return valueOf(token.name) ?? token.source;
    })
    .join("");
};

/**
 * Phase two: resolve `{$field}` against a transaction and unescape. `null`
 * means a field couldn't be read, matching the backend's "leave the target
 * alone" answer.
 */
export const renderBuiltins = (
  text: string,
  read: (field: FieldName) => string | null,
): string | null => {
  if (isPlain(text)) return text;
  const tokens = tokenize(text);
  if (!tokens) return null;

  let out = "";
  for (const token of tokens) {
    if (token.kind === "literal") {
      out += unescape(token.source);
      continue;
    }
    if (!token.name.startsWith(BUILTIN_PREFIX)) return null;
    const field = token.name.slice(BUILTIN_PREFIX.length) as FieldName;
    if (!TEMPLATE_BUILTINS.includes(field)) return null;
    const value = read(field);
    if (value === null) return null;
    out += value;
  }
  return out;
};

/**
 * Every problem with `text`. `declared` is the enclosing action set's input
 * names, or `null` in a rule's own action, where a bare name can only be a
 * `{$…}` written without its sigil.
 */
export const checkTemplate = (
  text: string,
  declared: readonly string[] | null,
): TemplateProblem[] => {
  if (isPlain(text)) return [];
  const tokens = tokenize(text);
  if (!tokens) return [{ type: "malformed", text }];

  const out: TemplateProblem[] = [];
  for (const token of tokens) {
    if (token.kind !== "placeholder") continue;
    const known = token.name.startsWith(BUILTIN_PREFIX)
      ? TEMPLATE_BUILTINS.includes(
          token.name.slice(BUILTIN_PREFIX.length) as FieldName,
        )
      : (declared?.includes(token.name) ?? false);
    if (
      !known &&
      !out.some((p) => p.type === "unknownPlaceholder" && p.name === token.name)
    ) {
      out.push({ type: "unknownPlaceholder", name: token.name });
    }
  }
  return out;
};

/** Does `text` reference `input`? Warns before a rename breaks the set. */
export const referencesInput = (text: string, input: string): boolean => {
  if (isPlain(text)) return false;
  const tokens = tokenize(text);
  return (
    tokens?.some((t) => t.kind === "placeholder" && t.name === input) ?? false
  );
};

const fillAction = (
  action: EditActionDto,
  valueOf: (name: string) => string | undefined,
): EditActionDto => {
  switch (action.field) {
    case "merchant":
    case "description":
      return {
        field: action.field,
        value: renderInputs(action.value, valueOf),
      };
    case "prompt":
      return {
        field: "prompt",
        value: {
          ...action.value,
          prefix:
            action.value.prefix === null
              ? null
              : renderInputs(action.value.prefix, valueOf),
        },
      };
    // A nested citation's values are written in the *outer* set's scope.
    case "applyActionSet":
      return {
        field: "applyActionSet",
        value: {
          set: action.value.set,
          inputs: action.value.inputs.map((input) => ({
            ...input,
            value: renderInputs(input.value, valueOf),
          })),
        },
      };
    case "conditional":
      return {
        field: "conditional",
        value: {
          ...action.value,
          action: fillAction(action.value.action, valueOf),
        },
      };
    default:
      return action;
  }
};

const fillInputs = (
  target: ActionSetDto,
  reference: ActionSetRef,
): EditActionDto[] => {
  const supplied = new Map(
    reference.inputs
      .filter((input) => input.value.trim() !== "")
      .map((input) => [input.name, input.value]),
  );
  // Only inputs the set still declares; a value left behind by a rename is
  // dropped, exactly as the backend drops it.
  const declared = new Set(target.inputs.map((input) => input.name));
  const valueOf = (name: string) =>
    declared.has(name) ? supplied.get(name) : undefined;

  return target.actions.map((action) => fillAction(action, valueOf));
};

/**
 * Replace every `applyActionSet` with the cited set's actions, inputs filled
 * in. Mirrors `expand_actions`: the body is spliced at the citation's position
 * so a later action in the rule still wins, and a set expanding to several
 * actions under a `conditional` is re-wrapped per action so the guard survives.
 */
export const expandActions = (
  actions: EditActionDto[],
  sets: readonly ActionSetDto[] | undefined,
  seen: ReadonlySet<ActionSetId> = new Set(),
): EditActionDto[] => {
  const out: EditActionDto[] = [];
  for (const action of actions) {
    if (action.field === "applyActionSet") {
      const target = sets?.find((s) => s.id === action.value.set);
      // A missing set, or a cycle that shouldn't be storable in the first
      // place: contribute nothing rather than guessing.
      if (!target || seen.has(target.id)) continue;
      const nested = new Set(seen).add(target.id);
      out.push(
        ...expandActions(fillInputs(target, action.value), sets, nested),
      );
      continue;
    }
    if (action.field === "conditional") {
      const inner = expandActions([action.value.action], sets, seen);
      for (const expanded of inner) {
        out.push({
          field: "conditional",
          value: { ...action.value, action: expanded },
        });
      }
      continue;
    }
    out.push(action);
  }
  return out;
};
